import { useCallback, useRef, useState } from "react";
import { useGlobalContext } from "../Context/GlobalContext";

import NoImg from "../Images/Item_Image_Placeholder.webp";
import ChaafoLogo from "../Images/Restaurant_Logo_Placeholder.webp";

const LIMIT = 12;
const THROTTLE_TIME = 1500;

// =========================================================
// GET FIRST IMAGE
// =========================================================

const getFirstImage = (photo) => {
  if (!photo) {
    return NoImg;
  }

  let imageUrl = "";

  if (Array.isArray(photo)) {
    imageUrl = photo.length > 0 ? String(photo[0] || "").trim() : "";
  } else {
    imageUrl = String(photo).split(",")[0].trim();
  }

  if (!imageUrl) {
    return NoImg;
  }

  if (imageUrl.startsWith("blob:")) {
    return NoImg;
  }

  if (!imageUrl.startsWith("http://") && !imageUrl.startsWith("https://")) {
    return NoImg;
  }

  return encodeURI(imageUrl);
};

const useOfferItems = () => {
  const { api } = useGlobalContext();

  // =========================================================
  // STATE
  // =========================================================

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);

  // =========================================================
  // PAGINATION / THROTTLE REFS
  // =========================================================

  const requestInProgressRef = useRef(false);

  const lastRequestTimeRef = useRef(0);

  const offsetRef = useRef(0);

  const hasMoreRef = useRef(true);

  // =========================================================
  // FETCH OFFER DATA
  // =========================================================

  const fetchOfferData = useCallback(
    async ({ text, latitude, longitude, reset = false }) => {
      // -----------------------------------------------------
      // REQUEST ALREADY RUNNING
      // -----------------------------------------------------

      if (requestInProgressRef.current) {
        console.log("Offer API skipped: request already in progress");

        return {
          success: false,
          throttled: true,
        };
      }

      // -----------------------------------------------------
      // NO MORE DATA
      // -----------------------------------------------------

      if (!reset && !hasMoreRef.current) {
        console.log("Offer API skipped: no more data");

        return {
          success: false,
          noMoreData: true,
        };
      }

      // -----------------------------------------------------
      // RESET PAGINATION
      // -----------------------------------------------------

      if (reset) {
        offsetRef.current = 0;

        hasMoreRef.current = true;

        setHasMore(true);

        setError("");
      }

      const currentOffset = offsetRef.current;

      // Lock immediately so another observer/call cannot start.
      requestInProgressRef.current = true;

      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        // ===================================================
        // THROTTLE PAGINATION
        // ===================================================

        if (!reset) {
          const elapsed = Date.now() - lastRequestTimeRef.current;

          if (elapsed < THROTTLE_TIME) {
            const remainingTime = THROTTLE_TIME - elapsed;

            await new Promise((resolve) => {
              window.setTimeout(resolve, remainingTime);
            });
          }
        }

        // ===================================================
        // API
        // ===================================================

        const response = await api.post("/Order/searchItemListAll", {
          latitude: String(latitude),

          longitude: String(longitude),

          // Distance/radius - pagination value nahi hai
          range: 10,

          text: String(text),

          limit: LIMIT,

          // 0 -> 12 -> 24 -> 36...
          offset: currentOffset,
        });

        console.log("======================================");

        console.log("OFFER API REQUEST:", {
          latitude: String(latitude),
          longitude: String(longitude),
          range: 10,
          text: String(text),
          limit: LIMIT,
          offset: currentOffset,
        });

        console.log("OFFER API RESPONSE:", response?.data);

        console.log("======================================");

        // ===================================================
        // RESTAURANTS
        // ===================================================

        const restaurants =
          response?.data?.responseCode === "00" &&
          Array.isArray(response?.data?.response)
            ? response.data.response
            : [];

        // ===================================================
        // RESTAURANT -> ITEMS
        // ===================================================

        const mappedItems = restaurants.flatMap(
          (restaurant, restaurantIndex) => {
            const restaurantItems = Array.isArray(restaurant?.items)
              ? restaurant.items
              : [];

            return restaurantItems.map((item, itemIndex) => {
              // ===========================================
              // ITEM IMAGE
              // ===========================================

              const itemImage = getFirstImage(item?.photo);

              // ===========================================
              // RESTAURANT
              // ===========================================

              const restaurantName =
                restaurant?.restaurant_name || "Restaurant";

              const restaurantLogoRaw = restaurant?.logo
                ? String(restaurant.logo).trim()
                : "";

              const restaurantImage =
                restaurantLogoRaw.startsWith("http://") ||
                restaurantLogoRaw.startsWith("https://")
                  ? encodeURI(restaurantLogoRaw)
                  : ChaafoLogo;

              // ===========================================
              // PRICES
              // ===========================================

              const prices = Array.isArray(item?.prices) ? item.prices : [];

              const firstPrice = prices.length > 0 ? prices[0] : null;

              const price = Number(firstPrice?.price || 0);

              const discountPrice = Number(firstPrice?.discount_price || 0);

              const formattedPrice =
                firstPrice?.formatted_price || (price > 0 ? `₹${price}` : "₹0");

              const formattedDiscountPrice =
                firstPrice?.formatted_discount_price || "";

              const hasDiscount =
                discountPrice > 0 && price > 0 && discountPrice < price;

              const finalFormattedPrice = hasDiscount
                ? formattedDiscountPrice
                : formattedPrice;

              // ===========================================
              // VEG / NON VEG
              // ===========================================

              const dishIcon =
                Array.isArray(item?.icon_dish) && item.icon_dish.length > 0
                  ? item.icon_dish[0]
                  : "";

              let dishType = "";

              try {
                const parsedDish =
                  typeof item?.dish === "string"
                    ? JSON.parse(item.dish)
                    : item?.dish;

                if (Array.isArray(parsedDish) && parsedDish.length > 0) {
                  dishType =
                    String(parsedDish[0]) === "1"
                      ? "Veg"
                      : String(parsedDish[0]) === "2"
                        ? "Non Veg"
                        : "";
                }
              } catch {
                dishType = "";
              }

              // ===========================================
              // UI MODEL
              // ===========================================

              return {
                id: item?.item_id || `${restaurantIndex}-${itemIndex}`,

                // Restaurant
                restaurantId: restaurant?.us_code || "",

                restaurantName,

                restaurantImage,

                // Restaurant open / closed status
                catalogueStatus: String(
                  restaurant?.catalogue_status ??
                    restaurant?.catalogueStatus ??
                    "",
                )
                  .trim()
                  .toLowerCase(),

                restaurantLatitude: restaurant?.latitude || "",

                restaurantLongitude: restaurant?.lontitude || "",

                // Item

                itemId: item?.item_id || "",

                itemName: item?.item_name || "Item",

                itemDescription: item?.item_description || "",

                itemImage,

                categoryName: item?.category_name || "",

                subCategory: item?.sub_category || "",

                ratings: Number(item?.ratings || 0),

                status: item?.status || "",

                inStock: item?.inStock === true,

                homeDeliveryAvailability:
                  item?.homeDeliveryAvailability === true,

                takeawayAvailability: item?.takeawayAvailability === true,

                diningAvailability: item?.diningAvailability === true,

                dishIcon,
                dishType,

                // Price

                prices,
                firstPrice,

                price,
                formattedPrice,

                discountPrice,
                formattedDiscountPrice,

                finalFormattedPrice,
                hasDiscount,

                size: firstPrice?.size || "",

                discount: firstPrice?.discount || item?.discount || "",
              };
            });
          },
        );
        // ===================================================
        // HIDE OUT OF STOCK ITEMS
        // ===================================================

        const availableItems = mappedItems.filter(
          (item) => item?.inStock === true,
        );

        console.log("AVAILABLE OFFER ITEMS:", availableItems);
        console.log("TOTAL AVAILABLE ITEMS:", availableItems.length);

        console.log("MAPPED OFFER ITEMS:", mappedItems);

        console.log("TOTAL MAPPED ITEMS:", mappedItems.length);

        // ===================================================
        // FIRST PAGE / NEXT PAGE
        // ===================================================

 if (reset) {
   setItems(availableItems);
 } else {
   setItems((previousItems) => {
     const combinedItems = [...previousItems, ...availableItems];

     return Array.from(
       new Map(
         combinedItems.map((item) => [
           `${item.restaurantId}-${item.itemId}`,
           item,
         ]),
       ).values(),
     );
   });
 }

        // ===================================================
        // PAGINATION
        // ===================================================

        // IMPORTANT:
        // API response restaurant-wise nested hai.
        // Isliye restaurants.length ko LIMIT se compare nahi karna.
        //
        // Jab tak API kuch items return kar rahi hai,
        // next offset try karenge.
        //
        // Jab next offset par zero items milenge,
        // tab pagination stop hogi.

        const receivedItemCount = mappedItems.length;

        const nextHasMore = receivedItemCount > 0;

        if (nextHasMore) {
          offsetRef.current = currentOffset + LIMIT;
        }

        hasMoreRef.current = nextHasMore;

        setHasMore(nextHasMore);

        console.log("PAGINATION:", {
          currentOffset,
          nextOffset: offsetRef.current,
          receivedItemCount,
          hasMore: nextHasMore,
        });

        return {
          success: true,

          items: mappedItems,

          offset: offsetRef.current,

          hasMore: nextHasMore,
        };
      } catch (error) {
        console.error("Offer API error:", error?.response?.data || error);

        const message =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch offer data.";

        // Initial request failure par page error show karo.
        // Pagination failure par already loaded items mat hatao.
        if (reset) {
          setError(message);

          setItems([]);
        }

        return {
          success: false,

          items: [],

          message,
        };
      } finally {
        requestInProgressRef.current = false;

        lastRequestTimeRef.current = Date.now();

        setLoading(false);

        setLoadingMore(false);
      }
    },
    [api],
  );

  return {
    items,

    loading,

    loadingMore,

    error,

    hasMore,

    fetchOfferData,
  };
};

export default useOfferItems;
